import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, Video, ExternalLink, CalendarClock, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const CUTOFF_HOURS = 24;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export const SlotPicker = ({ token, onBooked, onSkip, base = "booking", reschedule = false, bookingId = null }) => {
  const [data, setData] = useState(null);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const slotsUrl = `${API}/${base}/${token}/slots${reschedule ? `?reschedule=true&exclude_booking_id=${bookingId || ""}` : ""}`;
  const postUrl = reschedule ? `${API}/${base}/${token}/reschedule` : `${API}/${base}/${token}`;

  useEffect(() => {
    axios
      .get(slotsUrl)
      .then((res) => (res.data.booked ? onBooked(res.data.booked) : setData(res.data)))
      .catch(() => setError("Could not load availability. Please refresh."));
  }, [slotsUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const map = new Map();
    (data?.slots || []).forEach((iso) => {
      const key = new Date(iso).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(iso);
    });
    return [...map.entries()];
  }, [data]);

  useEffect(() => {
    if (!day && groups.length) setDay(groups[0][0]);
  }, [groups, day]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(postUrl, { start: slot, timezone: TZ, booking_id: bookingId });
      onBooked(res.data.booking);
    } catch (err) {
      const d = err.response?.data;
      const detail = d && typeof d === "object" && typeof d.detail === "string" ? d.detail : null;
      const clean = detail && detail.length < 200 && !/[<>]|html|cloudflare|origin web server|gateway/i.test(detail);
      setError(
        clean
          ? detail
          : "We're finalizing your booking, this can take a moment. Please wait a few seconds, then tap Confirm again. If your calendar invite doesn't arrive by email shortly, contact Rhea and she'll confirm it.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <p data-testid="slots-loading" className="text-sm text-[#9CA3AF]">
        {error || "Checking Rhea's calendar…"}
      </p>
    );
  }

  const times = groups.find(([k]) => k === day)?.[1] || [];

  return (
    <div data-testid="slot-picker" className="border border-[#E5E7EB] p-6 bg-white">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">
        <CalendarDays size={14} /> {reschedule ? "Pick a new time" : `Pick your ${data.duration_minutes} minute session`}
      </div>
      <p className="text-xs text-[#9CA3AF] mt-2">Times shown in your timezone ({TZ}).</p>
      <p data-testid="slots-weekly-note" className="text-xs text-[#9CA3AF] mt-1">We provide one session per week, so your sessions are spaced at least 7 days apart.</p>
      {data.demo && (
        <p data-testid="slots-demo-note" className="text-xs text-[#3A4A63] bg-[#F3F4F6] px-4 py-3 mt-3">
          Preview availability. Google Calendar sync activates once Rhea connects her account.
        </p>
      )}
      <div className="flex gap-2 overflow-x-auto mt-5 pb-2">
        {groups.map(([key, list]) => (
          <button
            key={key}
            data-testid={`slot-day-${key.replace(/\s/g, "-")}`}
            onClick={() => { setDay(key); setSlot(null); }}
            className={`shrink-0 border px-4 py-3 text-sm font-semibold transition-colors ${
              day === key ? "bg-[#041C3E] border-[#041C3E] text-white" : "border-[#E5E7EB] hover:border-[#041C3E]"
            }`}
          >
            {fmtDate(list[0])}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4">
        {times.map((iso) => (
          <button
            key={iso}
            data-testid={`slot-time-${iso}`}
            onClick={() => setSlot(iso)}
            className={`border px-3 py-3 text-sm font-head font-bold transition-colors ${
              slot === iso ? "bg-[#D94833] border-[#D94833] text-white" : "border-[#E5E7EB] hover:border-[#041C3E]"
            }`}
          >
            {fmtTime(iso)}
          </button>
        ))}
      </div>
      {error && <p data-testid="booking-error" className="text-[#D94833] text-sm mt-4">{error}</p>}
      <div className="flex flex-wrap gap-4 mt-6">
        <button
          data-testid={reschedule ? "reschedule-confirm-button" : "booking-confirm-button"}
          disabled={!slot || busy}
          onClick={submit}
          className="bg-[#D94833] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors duration-300 disabled:opacity-40"
        >
          {busy ? (reschedule ? "Rescheduling…" : "Finalizing your booking…") : slot ? `Confirm ${fmtDate(slot)} · ${fmtTime(slot)}` : "Select a time"}
        </button>
        {onSkip && (
          <button
            data-testid={reschedule ? "reschedule-cancel-button" : "booking-skip-button"}
            onClick={onSkip}
            className="text-sm font-semibold text-[#3A4A63] hover:text-[#041C3E] underline underline-offset-4"
          >
            {reschedule ? "Keep my current time" : "Book later, start the assessment"}
          </button>
        )}
      </div>
    </div>
  );
};

export const BookingCard = ({ booking, token, onChanged, maxReschedules = 0 }) => {
  const [mode, setMode] = useState("view");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const manageable = Boolean(token && onChanged);
  const changeable = new Date(booking.start).getTime() - Date.now() > CUTOFF_HOURS * 3600 * 1000;
  const maxR = maxReschedules || booking.max_reschedules || 0;
  const remaining = Math.max(0, maxR - (booking.reschedule_count || 0));

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/booking/${token}/cancel`, { booking_id: booking.id });
      onChanged(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not cancel. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (mode === "reschedule") {
    return (
      <div data-testid="reschedule-picker">
        <SlotPicker
          token={token}
          reschedule
          bookingId={booking.id}
          onBooked={(b) => { setMode("view"); onChanged(b); }}
          onSkip={() => setMode("view")}
        />
      </div>
    );
  }

  return (
    <div data-testid="booking-card" className="border border-[#041C3E] p-6 bg-white">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">
        {booking.session_number ? `Session ${booking.session_number}` : "Session booked"}
      </p>
      <p className="font-head text-2xl font-bold mt-2">
        {fmtDate(booking.start)} · {fmtTime(booking.start)} to {fmtTime(booking.end)}
      </p>
      <p className="text-xs text-[#9CA3AF] mt-1">Shown in your timezone ({TZ}). Invite sent to {booking.email}.</p>
      <div className="flex flex-wrap gap-3 mt-4">
        {booking.meet_link && (
          <a data-testid="booking-meet-link" href={booking.meet_link} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 bg-[#041C3E] text-white px-5 py-3 text-sm font-semibold hover:bg-[#D94833] transition-colors">
            <Video size={14} /> Join Google Meet
          </a>
        )}
        {booking.event_link && (
          <a data-testid="booking-event-link" href={booking.event_link} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 border border-[#041C3E] px-5 py-3 text-sm font-semibold hover:bg-[#041C3E] hover:text-white transition-colors">
            <ExternalLink size={14} /> View in Google Calendar
          </a>
        )}
        {booking.status === "demo" && (
          <p data-testid="booking-demo-note" className="text-xs text-[#3A4A63] bg-[#F3F4F6] px-4 py-3">
            Google Calendar isn't connected yet. Rhea will send the calendar invite and Meet link manually.
          </p>
        )}
      </div>
      {manageable && (
        <div className="mt-5 pt-5 border-t border-[#E5E7EB]">
          {changeable ? (
            <div className="flex flex-wrap gap-3 items-center">
              <button
                data-testid="booking-reschedule-button"
                onClick={() => setMode("reschedule")}
                disabled={remaining <= 0}
                title={remaining <= 0 ? "No reschedules left for this session" : ""}
                className="inline-flex items-center gap-2 border border-[#041C3E] px-5 py-3 text-sm font-semibold hover:bg-[#041C3E] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#041C3E]">
                <CalendarClock size={14} /> Reschedule
              </button>
              <span data-testid="reschedules-left" className="text-xs text-[#9CA3AF]">
                {remaining > 0 ? `${remaining} reschedule${remaining > 1 ? "s" : ""} left` : "No reschedules left"}
              </span>
              <button
                data-testid="booking-cancel-button"
                disabled={busy}
                onClick={() => { if (window.confirm("Cancel this session? This frees the slot and removes the calendar invite.")) cancel(); }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#D94833] hover:underline underline-offset-4 disabled:opacity-40">
                <XCircle size={14} /> {busy ? "Cancelling…" : "Cancel session"}
              </button>
            </div>
          ) : (
            <p data-testid="booking-locked-note" className="text-xs text-[#3A4A63] bg-[#F3F4F6] px-4 py-3">
              Changes close {CUTOFF_HOURS} hours before your session. To move or cancel now, please email Rhea directly.
            </p>
          )}
          {error && <p data-testid="manage-error" className="text-[#D94833] text-sm mt-3">{error}</p>}
        </div>
      )}
    </div>
  );
};

export const SessionScheduler = ({ token, heading = "Your sessions" }) => {
  const [state, setState] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    axios.get(`${API}/booking/${token}/slots`)
      .then((r) => { setState(r.data); setAdding(false); })
      .catch(() => setError("Could not load your sessions. Please refresh."));

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) {
    return <p data-testid="scheduler-loading" className="text-sm text-[#9CA3AF]">{error || "Loading your sessions…"}</p>;
  }

  const { bookings, max_sessions, max_reschedules, can_book_more } = state;
  const showPicker = adding || bookings.length === 0;

  return (
    <div data-testid="session-scheduler" className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">{heading}</p>
        <span data-testid="session-count" className="text-xs text-[#9CA3AF]">
          {bookings.length} of {max_sessions} scheduled
        </span>
      </div>
      {bookings.map((b) => (
        <BookingCard key={b.id} booking={b} token={token} maxReschedules={max_reschedules} onChanged={load} />
      ))}
      {can_book_more && showPicker && (
        <div>
          {bookings.length > 0 && (
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833] mb-3">Book your next session</p>
          )}
          <SlotPicker token={token} onBooked={load} onSkip={bookings.length ? () => setAdding(false) : undefined} />
        </div>
      )}
      {can_book_more && !showPicker && (
        <button data-testid="book-another-button" onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 bg-[#041C3E] text-white px-5 py-3 text-sm font-semibold hover:bg-[#D94833] transition-colors">
          <CalendarDays size={14} /> Book another session
        </button>
      )}
      {!can_book_more && (
        <p data-testid="sessions-complete-note" className="text-xs text-[#3A4A63] bg-[#F3F4F6] px-4 py-3">
          You have scheduled all {max_sessions} session{max_sessions > 1 ? "s" : ""} included in your plan.
        </p>
      )}
    </div>
  );
};
